import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { FoxLogo } from './FoxLogo';
import { useAppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    cart
  } = useAppContext();
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Domains',
    href: '/domains'
  }, {
    name: 'Features',
    href: '#features'
  }, {
    name: 'Pricing',
    href: '#pricing'
  }, {
    name: 'Contact',
    href: '#contact'
  }];
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (href.startsWith('/')) {
      navigate(href);
      return;
    }
    if (location.pathname !== '/') {
      navigate('/');
      // Need a slight delay to allow navigation before scrolling
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-950/80 backdrop-blur-md border-b border-slate-800 py-3' : 'bg-transparent py-5'}`} data-id="element-1260">
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between" data-id="element-1261">
        <Link to="/" className="flex items-center gap-2 group" data-id="element-1262">
          <motion.div whileHover={{
          rotate: [0, -10, 10, -10, 0]
        }} transition={{
          duration: 0.5
        }} data-id="element-1263">
            <FoxLogo className="w-8 h-8 md:w-10 md:h-10" data-id="element-1264" />
          </motion.div>
          <span className="font-heading font-bold text-xl md:text-2xl text-white tracking-tight" data-id="element-1265">
            Fox Line <span className="text-fox-500" data-id="element-1266">Hosting</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-id="element-1267">
          {navLinks.map(link => <a key={link.name} href={link.href} onClick={e => handleNavClick(e, link.href)} className="text-sm font-medium text-slate-300 hover:text-fox-400 transition-colors" data-id="element-1268">
              {link.name}
            </a>)}
          <Link to="/cart" className="relative text-slate-300 hover:text-fox-400 transition-colors" data-id="element-1269">
            <ShoppingCart size={20} data-id="element-1270" />
            {cartCount > 0 && <motion.span initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} className="absolute -top-2 -right-2 w-5 h-5 bg-fox-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-id="element-1271">
                {cartCount}
              </motion.span>}
          </Link>
          <Link to="/login" className="bg-fox-600 hover:bg-fox-500 text-white px-5 py-2 rounded-full text-sm font-medium transition-all shadow-[0_0_15px_rgba(234,88,12,0.3)] hover:shadow-[0_0_25px_rgba(234,88,12,0.5)]" data-id="element-1272">
            Login
          </Link>
        </nav>

        {/* Mobile Toggle */}
        <div className="flex md:hidden items-center gap-4" data-id="element-1273">
          <Link to="/cart" className="relative text-slate-300 hover:text-fox-400 transition-colors" data-id="element-1274">
            <ShoppingCart size={20} data-id="element-1275" />
            {cartCount > 0 && <span className="absolute -top-2 -right-2 w-5 h-5 bg-fox-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-id="element-1276">
                {cartCount}
              </span>}
          </Link>
          <button className="text-slate-300 hover:text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-1277">
            {isMobileMenuOpen ? <X size={24} data-id="element-1278" /> : <Menu size={24} data-id="element-1279" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-1280">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-slate-900 border-b border-slate-800 overflow-hidden" data-id="element-1281">
            <div className="flex flex-col px-4 py-6 gap-4" data-id="element-1282">
              {navLinks.map(link => <a key={link.name} href={link.href} onClick={e => handleNavClick(e, link.href)} className="text-lg font-medium text-slate-300 hover:text-fox-400 py-2 border-b border-slate-800" data-id="element-1283">
                  {link.name}
                </a>)}
              <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="bg-fox-600 text-white px-5 py-3 rounded-lg text-center font-medium mt-4" data-id="element-1284">
                Login to Dashboard
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}